import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Toolbar,
  Tooltip,
  Collapse,
  Drawer,
} from "@mui/material";

import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";

import { useDispatch, useSelector } from "react-redux";

import CartIcon from "./CartIcon";
import ProductList from "./ProductList";
import CartHoverPreview from "./CartHoverPreview";

import { logoutUser } from "../features/userSlice";
import { CLEAR_CART } from "../features/productInCartSlice";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";

import Search from "./Search";
import logo from "../assets/logo.jpg";

export default function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loggedUser = useSelector(
    (state) => state.user.logedUser
  );

  const cart = useSelector(
    (state) => state.cart.items
  );

  const [userMenuAnchor, setUserMenuAnchor] =
    useState(null);

  const [cartAnchor, setCartAnchor] =
    useState(null);

  const [showCartPreview, setShowCartPreview] =
    useState(false);

  const [mobileCategoriesOpen, setMobileCategoriesOpen] =
    useState(false);

  const [mobileSearchOpen, setMobileSearchOpen] =
    useState(false);

  const closeCartTimer = useRef(null);

  const isUserMenuOpen = Boolean(userMenuAnchor);

  const handleUserIconClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    dispatch(CLEAR_CART());

    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpirationTime");

    localStorage.removeItem("loggedUser");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("cart");

    handleUserMenuClose();
    navigate("/");
  };

  const openCartPreview = (element) => {
    if (closeCartTimer.current) {
      clearTimeout(closeCartTimer.current);
    }

    setCartAnchor(element);
    setShowCartPreview(true);
  };

  const closeCartPreview = () => {
    closeCartTimer.current = setTimeout(() => {
      setShowCartPreview(false);
      setCartAnchor(null);
    }, 150);
  };

  const handleCartClick = () => {
    setShowCartPreview(false);
    setCartAnchor(null);
    navigate("/cart");
  };

  const handleCartKeyDown = (event) => {
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      handleCartClick();
    }

    if (event.key === "Escape") {
      setShowCartPreview(false);
      setCartAnchor(null);
    }
  };

  const userButtonLabel = loggedUser?.name
    ? `פתיחת תפריט המשתמש של ${loggedUser.name}`
    : "פתיחת תפריט המשתמש";

  const cartItemsCount = Array.isArray(cart)
    ? cart.reduce(
      (total, item) =>
        total + Number(item?.amount || 0),
      0
    )
    : 0;

  const cartButtonLabel =
    cartItemsCount > 0
      ? `פתיחת עגלת הקניות, ${cartItemsCount} פריטים`
      : "פתיחת עגלת הקניות, העגלה ריקה";

  return (
   <AppBar
  component="div"
  position="sticky"
  color="transparent"
  elevation={0}
  aria-label="סרגל קטגוריות ופעולות האתר"
  sx={{
    /*
     * בטלפון הסרגל תמיד קבוע.
     * במחשב הוא נדבק רק לאחר שמגיעים אליו בגלילה.
     */
    position: {
      xs: "fixed",
      md: "sticky",
    },

    top: 0,
    right: 0,
    left: 0,
    width: "100%",

    bgcolor: {
      xs: "#ffffff",
      md: "transparent",
    },

    pt: {
      xs: 0,
      md: 1,
    },

    zIndex: (theme) =>
      theme.zIndex.appBar + 10,

    "& a:focus-visible, & button:focus-visible": {
      outline: "3px solid #D81B60",
      outlineOffset: "4px",
      borderRadius: "6px",
    },
  }}
>
      {/* כותרת טלפון */}
      <Box
        sx={{
          display: {
            xs: "block",
            md: "none",
          },
          width: "100%",
          overflow: "hidden",
        }}
      >
       <Paper
  elevation={3}
  sx={{
    width: "100%",
    borderRadius: 0,
    backgroundColor: "#ffffff",

    // פס ורוד דק מתחת לכל סרגל הכלים בטלפון
   borderBottom: "1px solid #D81B60",
  }}
>
          <Toolbar
            disableGutters
            sx={{
              minHeight: 56,
              px: 0.5,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
            }}
          >
            {/* צד ימין: חזרה וקטגוריות */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <IconButton
                type="button"
                onClick={() => navigate(-1)}
                aria-label="חזרה לעמוד הקודם"
              >
                <ArrowBackIcon />
              </IconButton>

              <IconButton
                type="button"
                onClick={() =>
                  setMobileCategoriesOpen(true)
                }
                aria-label="פתיחת תפריט הקטגוריות"
                aria-expanded={mobileCategoriesOpen}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            {/* לוגו במרכז */}
            <Box
              component={Link}
              to="/"
              aria-label="מעבר לדף הבית"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="שיינסטוק"
                sx={{
                  display: "block",
                  width: 72,
                  maxHeight: 44,
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* צד שמאל: חיפוש, משתמש ועגלה */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                direction: "rtl",
                order: 1,

                "& .MuiIconButton-root": {
                  width: 44,
                  height: 44,
                  padding: "8px",
                  color: "#58A76B",
                },

                "& .MuiSvgIcon-root": {
                  width: 28,
                  height: 28,
                  fontSize: 28,
                  color: "#58A76B",
                },
              }}
            >
              <IconButton
                type="button"
                aria-label={
                  mobileSearchOpen
                    ? "סגירת החיפוש"
                    : "פתיחת החיפוש"
                }
                aria-expanded={mobileSearchOpen}
                onClick={() =>
                  setMobileSearchOpen((current) => !current)
                }
              >
                <SearchIcon
                  sx={{
                    fontSize: 28,
                    color: "#000000",
                  }}
                />
              </IconButton>

              {loggedUser ? (
                <IconButton
                  id="mobile-user-menu-button"
                  type="button"
                  onClick={handleUserIconClick}
                  aria-label={userButtonLabel}
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                >
                  <PersonIcon
                    sx={{
                      fontSize: 28,
                      color: "#000000",
                    }}
                  />
                </IconButton>
              ) : (
                <IconButton
                  component={Link}
                  to="/signIn"
                  aria-label="מעבר לעמוד ההתחברות"
                >
                  <PersonIcon
                    sx={{
                      fontSize: 28,
                      color: "#000000",
                    }}
                  />
                </IconButton>
              )}

              <IconButton
                type="button"
                onClick={handleCartClick}
                aria-label={cartButtonLabel}
              >
                <CartIcon
                  sx={{
                    fontSize: 28,
                    color: "#000000",
                  }}
                />
              </IconButton>
            </Box>
          </Toolbar>

          {/* שדה החיפוש נפתח מתחת לכותרת */}
          <Collapse in={mobileSearchOpen}>
            <Box
              sx={{
                px: 1.5,
                pb: 1.5,

                "& form": {
                  width: "100%",
                },

                "& .MuiTextField-root": {
                  flex: 1,
                  mr: 0,
                },
              }}
            >
              <Search fromNavBar />
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* מגירת הקטגוריות בטלפון */}
      <Drawer
        anchor="right"
        open={mobileCategoriesOpen}
        onClose={() =>
          setMobileCategoriesOpen(false)
        }
        sx={{
          zIndex: (theme) => theme.zIndex.drawer,

          "& .MuiDrawer-paper": {
            width: "min(330px, 88vw)",
            height: "100dvh",
            top: 0,
            margin: 0,
            direction: "rtl",
            backgroundColor: "#ffffff !important",
            color: "#000000",
            borderRadius: "0 !important",
            overflowX: "hidden",
          },
        }}
      >
        {/* כותרת המגירה */}
        <Box
          sx={{
            p: 2,
            minHeight: 64,
            borderBottom: "1px solid #dddddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#ffffff",
            color: "#D81B60",
            fontSize: "1.2rem",
            fontWeight: 700,
          }}
        >
          <Box component="span">
            קטגוריות
          </Box>

          <IconButton
            type="button"
            onClick={() =>
              setMobileCategoriesOpen(false)
            }
            aria-label="סגירת תפריט הקטגוריות"
            sx={{
              color: "#000000",
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        {/* רשימת הקטגוריות */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "#ffffff",

            "& button": {
              color: "#D81B60",
            },
          }}
        >
          <ProductList
            mobile
            onNavigate={() =>
              setMobileCategoriesOpen(false)
            }
          />
        </Box>
      </Drawer>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          display: {
            xs: "none",
            md: "block",
          },
        }}
      >
        <Box
          sx={{
            width: "min(1400px, 100%)",
            mx: "auto",
            px: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Paper
            elevation={6}
            sx={{
              flex: 1,
              borderRadius: 6,
              bgcolor: "#ffffff",
              overflow: "visible",
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                px: {
                  xs: 1,
                  sm: 2,
                },
                gap: {
                  xs: 1,
                  sm: 2,
                },
                flexWrap: "wrap",
                justifyContent:
                  "space-between",
                minHeight: {
                  xs: 48,
                  sm: 56,
                  md: 64,
                },
                transition:
                  "min-height 0.3s ease",
              }}
            >
              {/* רשימת המוצרים / הניווט */}
              <Box
                component="nav"
                aria-label="ניווט בין קטגוריות ומוצרים"
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  minWidth: 0,
                  "& a:focus-visible, & button:focus-visible":
                  {
                    outline:
                      "3px solid #D81B60",
                    outlineOffset: "3px",
                    borderRadius: "5px",
                  },
                }}
              >
                <ProductList />
              </Box>

              {/* כפתורי משתמש, מנהל ועגלה */}
              <Box
                component="nav"
                aria-label="פעולות משתמש"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {loggedUser?.tipeUser ===
                  "admin" && (
                    <Tooltip
                      title="מעבר לאזור הניהול"
                      arrow
                    >
                      <IconButton
                        component={Link}
                        to="/admin"
                        color="inherit"
                        aria-label="מעבר לאזור הניהול"
                        sx={{
                          color: "inherit",

                          "&:hover": {
                            color: "#111010",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <AdminPanelSettingsIcon
                          aria-hidden="true"
                          sx={{
                            fontSize: {
                              xs: 30,
                              md: 36,
                            },
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                {loggedUser ? (
                  <>
                    <Tooltip
                      title={
                        loggedUser?.name
                          ? `שלום, ${loggedUser.name}`
                          : "החשבון שלי"
                      }
                      arrow
                    >
                      <IconButton
                        id="user-menu-button"
                        color="inherit"
                        onClick={
                          handleUserIconClick
                        }
                        aria-label={
                          userButtonLabel
                        }
                        aria-controls={
                          isUserMenuOpen
                            ? "user-menu"
                            : undefined
                        }
                        aria-haspopup="menu"
                        aria-expanded={
                          isUserMenuOpen
                            ? "true"
                            : undefined
                        }
                      >
                        <PersonIcon
                          aria-hidden="true"
                          sx={{
                            fontSize: {
                              xs: 30,
                              md: 36,
                            },
                            color: "#58A76B",
                          }}
                        />
                      </IconButton>
                    </Tooltip>

                    <Menu
                      id="user-menu"
                      anchorEl={
                        userMenuAnchor
                      }
                      open={isUserMenuOpen}
                      onClose={
                        handleUserMenuClose
                      }
                      MenuListProps={{
                        "aria-labelledby":
                          "user-menu-button",
                        autoFocusItem:
                          isUserMenuOpen,
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem
                        onClick={
                          handleLogout
                        }
                      >
                        התנתקות
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Tooltip
                    title="התחברות"
                    arrow
                  >
                    <IconButton
                      color="inherit"
                      component={Link}
                      to="/signIn"
                      aria-label="מעבר לעמוד ההתחברות"
                    >
                      <PersonIcon
                        aria-hidden="true"
                        sx={{
                          fontSize: {
                            xs: 30,
                            md: 36,
                          },
                          color: "#58A76B",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}

                {/* עגלת קניות */}
                <Box
                  onMouseEnter={(event) =>
                    openCartPreview(
                      event.currentTarget
                    )
                  }
                  onMouseLeave={
                    closeCartPreview
                  }
                  onFocus={(event) =>
                    openCartPreview(
                      event.currentTarget
                    )
                  }
                  onBlur={(event) => {
                    const nextElement =
                      event.relatedTarget;

                    if (
                      !event.currentTarget.contains(
                        nextElement
                      )
                    ) {
                      closeCartPreview();
                    }
                  }}
                >
                  <Tooltip
                    title="עגלת קניות"
                    arrow
                  >
                    <IconButton
                      id="cart-button"
                      color="inherit"
                      onClick={
                        handleCartClick
                      }
                      onKeyDown={
                        handleCartKeyDown
                      }
                      aria-label={
                        cartButtonLabel
                      }
                      aria-haspopup="dialog"
                      aria-expanded={
                        showCartPreview
                      }
                      aria-controls={
                        showCartPreview
                          ? "cart-preview"
                          : undefined
                      }
                    >
                      <CartIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Popper
                  id="cart-preview"
                  open={showCartPreview}
                  anchorEl={cartAnchor}
                  placement="bottom-start"
                  role="dialog"
                  aria-label="תצוגה מקדימה של עגלת הקניות"
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                  sx={{
                    zIndex: 99999,
                  }}
                >
                  <Box
                    onMouseEnter={() => {
                      if (
                        closeCartTimer.current
                      ) {
                        clearTimeout(
                          closeCartTimer.current
                        );
                      }

                      setShowCartPreview(
                        true
                      );
                    }}
                    onMouseLeave={
                      closeCartPreview
                    }
                    onFocus={() => {
                      if (
                        closeCartTimer.current
                      ) {
                        clearTimeout(
                          closeCartTimer.current
                        );
                      }

                      setShowCartPreview(
                        true
                      );
                    }}
                    onBlur={(event) => {
                      const nextElement =
                        event.relatedTarget;

                      if (
                        !event.currentTarget.contains(
                          nextElement
                        )
                      ) {
                        closeCartPreview();
                      }
                    }}
                    sx={{
                      "& a:focus-visible, & button:focus-visible":
                      {
                        outline:
                          "3px solid #D81B60",
                        outlineOffset:
                          "3px",
                        borderRadius:
                          "5px",
                      },
                    }}
                  >
                    <CartHoverPreview
                      cart={cart || []}
                    />
                  </Box>
                </Popper>
              </Box>
            </Toolbar>
          </Paper>
        </Box>
      </Container>
    </AppBar>
  );
}